import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Button, Col, Form, Nav, Row } from "react-bootstrap";
import axios from "../axios";
import { AdminAddButton, AdminFormActions, AdminFormModal, RecordActions } from "../components/AdminForm";
import AdminCompany from "../components/AdminCompany";
import AdminEvents from "../components/AdminEvents";
import AdminTypes from "../components/AdminTypes";
import AdminHome from "../components/AdminHome";
import AdminShow from "../components/AdminShow";
import AdminVip from "../components/AdminVip";
import { removePrompt, useConfirm } from "../components/ConfirmDialog";
import Message from "../components/Message";

function authConfig(token, json = false) {
  const headers = { Authorization: `Bearer ${token}` };
  if (json) {
    headers["Content-Type"] = "application/json";
  }
  return { headers };
}

const classTypeLabel = {
  all: "All",
  automobile: "Automobile",
  motorcycle: "Motorcycle",
};

function typeLabel(types) {
  return (types || [])
    .map((item) => classTypeLabel[item])
    .filter(Boolean)
    .join(", ");
}

function makeMatchesClass(make, classType) {
  if (classType === "all") {
    return true;
  }
  return (make.vehicle_types || []).includes(classType);
}

function errorText(error) {
  return error.response && error.response.data.detail
    ? error.response.data.detail
    : error.message;
}

function AdminDashboardScreen() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const [makes, setMakes] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [classes, setClasses] = useState([]);
  const [makeTitle, setMakeTitle] = useState("");
  const [makeHistory, setMakeHistory] = useState("");
  const [makeTypes, setMakeTypes] = useState(["automobile"]);
  const [makeLogo, setMakeLogo] = useState(null);
  const [editingMake, setEditingMake] = useState(null);
  const [makeFormKey, setMakeFormKey] = useState(0);
  const [clubForm, setClubForm] = useState({
    title: "",
    description: "",
    location: "",
    website: "",
    email1: "",
    email2: "",
    email3: "",
    email4: "",
    phone: "",
  });
  const [clubLogo, setClubLogo] = useState(null);
  const [editingClub, setEditingClub] = useState(null);
  const [clubFormKey, setClubFormKey] = useState(0);
  const [editingClass, setEditingClass] = useState(null);
  const [classTitle, setClassTitle] = useState("");
  const [classType, setClassType] = useState("all");
  const [yearStart, setYearStart] = useState("");
  const [yearEnd, setYearEnd] = useState("");
  const [classMakes, setClassMakes] = useState([]);
  const [classConcours, setClassConcours] = useState(false);
  const [classFilter, setClassFilter] = useState("all");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [makeOpen, setMakeOpen] = useState(false);
  const [clubOpen, setClubOpen] = useState(false);
  const [classOpen, setClassOpen] = useState(false);
  const [ask, confirmDialog] = useConfirm();

  const loadAll = async (token) => {
    const config = authConfig(token);
    const [makeData, clubData, classData] = await Promise.all([
      axios.get("/api/admin/manufactures/", config),
      axios.get("/api/admin/clubs/", config),
      axios.get("/api/admin/car-classes/", config),
    ]);
    setMakes(makeData.data);
    setClubs(clubData.data);
    setClasses(classData.data);
  };

  useEffect(() => {
    if (!userInfo) {
      navigate("/login");
      return;
    }
    if (!userInfo.isAdmin) {
      navigate("/");
      return;
    }
    loadAll(userInfo.token).catch((err) => setError(errorText(err)));
  }, [navigate, userInfo]);

  const sections = [
    { id: "manufactures", label: "Manufactures", icon: "fa-industry" },
    { id: "clubs", label: "Clubs", icon: "fa-users" },
    { id: "car-classes", label: "Car classes", icon: "fa-layer-group" },
    { id: "types", label: "Types", icon: "fa-list-check" },
    { id: "events", label: "Events", icon: "fa-calendar-days" },
    { id: "home", label: "Home page", icon: "fa-house" },
    { id: "show", label: "Show page", icon: "fa-images" },
    { id: "company", label: "About Us", icon: "fa-building" },
    { id: "vip", label: "VIP luncheon", icon: "fa-utensils" },
  ];
  const requested = searchParams.get("section");
  const section = sections.some((item) => item.id === requested) ? requested : "manufactures";

  const openSection = (id) => {
    setMakeOpen(false);
    setClubOpen(false);
    setClassOpen(false);
    setSearchParams({ section: id });
  };

  const setClubField = (field, value) => {
    setClubForm((current) => ({ ...current, [field]: value }));
  };

  const toggleMake = (id) => {
    setClassMakes((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
    );
  };

  const visibleMakes = makes.filter((item) => makeMatchesClass(item, classType));

  const selectAllMakes = () => {
    const ids = visibleMakes.map((item) => item.id);
    const allSelected = ids.length > 0 && ids.every((id) => classMakes.includes(id));
    setClassMakes(allSelected ? [] : ids);
  };

  const changeClassType = (value) => {
    setClassType(value);
    setClassMakes((current) =>
      current.filter((id) => {
        const make = makes.find((item) => item.id === id);
        return make && makeMatchesClass(make, value);
      })
    );
  };

  const toggleMakeType = (value) => {
    setMakeTypes((current) =>
      current.includes(value) ? current.filter((item) => item !== value) : [...current, value]
    );
  };

  const clearClassForm = () => {
    setEditingClass(null);
    setClassTitle("");
    setClassType("all");
    setYearStart("");
    setYearEnd("");
    setClassMakes([]);
    setClassConcours(false);
  };

  const editClass = (item) => {
    setEditingClass(item);
    setClassTitle(item.title);
    setClassType(item.vehicle_type || "all");
    setYearStart(item.year_start ?? "");
    setYearEnd(item.year_end ?? "");
    setClassMakes(item.manufactures || []);
    setClassConcours(Boolean(item.concours));
    setError("");
    setNotice("");
    setClassOpen(true);
  };

  const clearMakeForm = () => {
    setEditingMake(null);
    setMakeTitle("");
    setMakeHistory("");
    setMakeTypes(["automobile"]);
    setMakeLogo(null);
    setMakeFormKey((current) => current + 1);
  };

  const editMake = (item) => {
    setEditingMake(item);
    setMakeTitle(item.title);
    setMakeHistory(item.history || "");
    setMakeTypes(item.vehicle_types?.length ? item.vehicle_types : ["automobile"]);
    setMakeLogo(null);
    setMakeFormKey((current) => current + 1);
    setError("");
    setNotice("");
    setMakeOpen(true);
  };

  const addMake = async (event) => {
    event.preventDefault();
    setError("");
    setNotice("");
    if (makeTypes.length === 0) {
      setError("Choose automobile, motorcycle, or both.");
      return;
    }
    const formData = new FormData();
    formData.append("title", makeTitle);
    formData.append("history", makeHistory);
    makeTypes.forEach((item) => formData.append("vehicle_types", item));
    if (makeLogo) {
      formData.append("logo", makeLogo);
    }
    const url = editingMake ? `/api/admin/manufactures/${editingMake.id}/` : "/api/admin/manufactures/";
    try {
      if (editingMake) {
        await axios.put(url, formData, authConfig(userInfo.token));
      } else {
        await axios.post(url, formData, authConfig(userInfo.token));
      }
      clearMakeForm();
      setMakeOpen(false);
      setNotice("Make saved.");
      await loadAll(userInfo.token);
    } catch (err) {
      setError(errorText(err));
    }
  };

  const emptyClub = {
    title: "",
    description: "",
    location: "",
    website: "",
    email1: "",
    email2: "",
    email3: "",
    email4: "",
    phone: "",
  };

  const clearClubForm = () => {
    setEditingClub(null);
    setClubForm(emptyClub);
    setClubLogo(null);
    setClubFormKey((current) => current + 1);
  };

  const editClub = (item) => {
    setEditingClub(item);
    setClubForm({
      title: item.title || "",
      description: item.description || "",
      location: item.location || "",
      website: item.website || "",
      email1: item.email1 || "",
      email2: item.email2 || "",
      email3: item.email3 || "",
      email4: item.email4 || "",
      phone: item.phone || "",
    });
    setClubLogo(null);
    setClubFormKey((current) => current + 1);
    setError("");
    setNotice("");
    setClubOpen(true);
  };

  const addClub = async (event) => {
    event.preventDefault();
    setError("");
    setNotice("");
    const formData = new FormData();
    Object.entries(clubForm).forEach(([key, value]) => formData.append(key, value));
    if (clubLogo) {
      formData.append("logo", clubLogo);
    }
    const editing = editingClub;
    try {
      if (editing) {
        await axios.put(`/api/admin/clubs/${editing.id}/`, formData, authConfig(userInfo.token));
      } else {
        await axios.post("/api/admin/clubs/", formData, authConfig(userInfo.token));
      }
      clearClubForm();
      setClubOpen(false);
      setNotice("Club saved.");
      await loadAll(userInfo.token);
    } catch (err) {
      setError(errorText(err));
    }
  };

  const addClass = async (event) => {
    event.preventDefault();
    setError("");
    setNotice("");
    const payload = {
      title: classTitle,
      vehicle_type: classType,
      year_start: yearStart === "" ? null : Number(yearStart),
      year_end: yearEnd === "" ? null : Number(yearEnd),
      concours: classConcours,
      manufactures: classMakes,
    };
    const url = editingClass ? `/api/admin/car-classes/${editingClass.id}/` : "/api/admin/car-classes/";
    try {
      if (editingClass) {
        await axios.put(url, payload, authConfig(userInfo.token, true));
      } else {
        await axios.post(url, payload, authConfig(userInfo.token, true));
      }
      clearClassForm();
      setClassOpen(false);
      setNotice("Car class saved.");
      await loadAll(userInfo.token);
    } catch (err) {
      setError(errorText(err));
    }
  };

  const removeRow = async (url, label, name) => {
    if (!(await ask(removePrompt(name || `this ${label}`)))) {
      return false;
    }
    setError("");
    setNotice("");
    try {
      await axios.delete(url, authConfig(userInfo.token));
      setNotice(`${label} removed.`);
      await loadAll(userInfo.token);
      return true;
    } catch (err) {
      setError(errorText(err));
      return false;
    }
  };

  const shownClasses = classes.filter((item) => {
    if (classFilter === "concours") {
      return item.concours;
    }
    if (classFilter === "other") {
      return !item.concours;
    }
    return true;
  });

  return (
    <Row className="dashboard g-4">
      {confirmDialog}
      <Col lg={3}>
        <div className="dashboard-nav">
          <div className="dashboard-user">
            <strong>Admin</strong>
            <div className="text-muted small">{userInfo?.email}</div>
          </div>
          <Nav variant="pills" className="flex-column">
            {sections.map((item) => (
              <Nav.Link
                key={item.id}
                active={section === item.id}
                href={`#${item.id}`}
                onClick={(event) => {
                  event.preventDefault();
                  openSection(item.id);
                }}
              >
                <i className={`fa-solid ${item.icon} me-2`}></i>
                {item.label}
              </Nav.Link>
            ))}
          </Nav>
        </div>
      </Col>
      <Col lg={9}>
        <div className="dashboard-panel">
          {notice && <Message variant="success">{notice}</Message>}
          {error && <Message variant="danger">{error}</Message>}

          {section === "manufactures" && (
            <>
        <h2>Manufactures</h2>
        <p>Makes members can choose when they add a car.</p>
        <AdminAddButton
          onClick={() => {
            clearMakeForm();
            setError("");
            setNotice("");
            setMakeOpen(true);
          }}
        >
          Add a make
        </AdminAddButton>
        <AdminFormModal
          show={makeOpen}
          title={editingMake ? `Edit ${editingMake.title}` : "Add a make"}
          onHide={() => {
            clearMakeForm();
            setMakeOpen(false);
          }}
        >
        <Form onSubmit={addMake}>
          <Form.Group className="mb-3" controlId="makeTitle">
            <Form.Label>Make</Form.Label>
            <Form.Control value={makeTitle} onChange={(e) => setMakeTitle(e.target.value)} required />
          </Form.Group>
          <Form.Group className="mb-3" controlId="makeHistory">
            <Form.Label>History</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={makeHistory}
              onChange={(e) => setMakeHistory(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3 make-builds">
            <Form.Label>Builds</Form.Label>
            <Form.Check
              type="checkbox"
              id="makeAutomobile"
              label="Automobile"
              checked={makeTypes.includes("automobile")}
              onChange={() => toggleMakeType("automobile")}
            />
            <Form.Check
              type="checkbox"
              id="makeMotorcycle"
              label="Motorcycle"
              checked={makeTypes.includes("motorcycle")}
              onChange={() => toggleMakeType("motorcycle")}
            />
            <p className="field-note">Select every type this make builds, or just one.</p>
          </Form.Group>
          <Form.Group className="mb-3" controlId="makeLogo">
            <Form.Label>Logo</Form.Label>
            <Form.Control
              key={makeFormKey}
              type="file"
              accept=".png,image/png"
              onChange={(e) => setMakeLogo(e.target.files?.[0] || null)}
            />
            <p className="field-note">
              PNG with a transparent background. Cropped from the center to 400×400 pixels, expanding or contracting until the last side touches the limit. The 150×150 thumbnail is a resize of that same image.
            </p>
            {editingMake ? (
              <p className="field-note">If you upload a new image, it replaces the current one.</p>
            ) : null}
          </Form.Group>
          <AdminFormActions
            saveLabel="Save make"
            onCancel={() => {
              clearMakeForm();
              setMakeOpen(false);
            }}
          />
        </Form>
        </AdminFormModal>
        {makes.length === 0 ? (
          <Message variant="info" className="mt-4">No makes yet.</Message>
        ) : (
          <div className="admin-records">
            {makes.map((item) => (
              <article key={item.id} className="admin-record">
                <div className="admin-record-logo">
                  {item.logo_thumb_url ? (
                    <img src={item.logo_thumb_url} alt="" />
                  ) : (
                    <span>{item.title}</span>
                  )}
                </div>
                <div className="admin-record-copy">
                  <strong>{item.title}</strong>
                  <span>{typeLabel(item.vehicle_types) || "Type not set"}</span>
                </div>
                <RecordActions
                  name={item.title}
                  onEdit={() => editMake(item)}
                  onRemove={() => removeRow(`/api/admin/manufactures/${item.id}/`, "make", item.title)}
                />
              </article>
            ))}
          </div>
        )}
            </>
          )}

          {section === "clubs" && (
            <>
        <h2>Clubs</h2>
        <p>Clubs kept for the site.</p>
        <AdminFormModal
          show={clubOpen}
          size="lg"
          title={editingClub ? `Edit ${editingClub.title}` : "Add a club"}
          onHide={() => {
            clearClubForm();
            setClubOpen(false);
          }}
        >
        <Form onSubmit={addClub}>
          <Form.Group className="mb-3" controlId="clubTitle">
            <Form.Label>Club name</Form.Label>
            <Form.Control
              value={clubForm.title}
              onChange={(e) => setClubField("title", e.target.value)}
              required
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="clubDescription">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={clubForm.description}
              onChange={(e) => setClubField("description", e.target.value)}
            />
          </Form.Group>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3" controlId="clubLocation">
                <Form.Label>Location</Form.Label>
                <Form.Control
                  value={clubForm.location}
                  onChange={(e) => setClubField("location", e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3" controlId="clubWebsite">
                <Form.Label>Website</Form.Label>
                <Form.Control
                  value={clubForm.website}
                  onChange={(e) => setClubField("website", e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3" controlId="email1">
                <Form.Label>Email 1</Form.Label>
                <Form.Control
                  type="email"
                  value={clubForm.email1}
                  onChange={(e) => setClubField("email1", e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3" controlId="email2">
                <Form.Label>Email 2</Form.Label>
                <Form.Control
                  type="email"
                  value={clubForm.email2}
                  onChange={(e) => setClubField("email2", e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3" controlId="email3">
                <Form.Label>Email 3</Form.Label>
                <Form.Control
                  type="email"
                  value={clubForm.email3}
                  onChange={(e) => setClubField("email3", e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3" controlId="email4">
                <Form.Label>Email 4</Form.Label>
                <Form.Control
                  type="email"
                  value={clubForm.email4}
                  onChange={(e) => setClubField("email4", e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-3" controlId="clubPhone">
            <Form.Label>Phone</Form.Label>
            <Form.Control value={clubForm.phone} onChange={(e) => setClubField("phone", e.target.value)} />
          </Form.Group>
          <Form.Group className="mb-3" controlId="clubLogo">
            <Form.Label>Logo</Form.Label>
            <Form.Control
              key={clubFormKey}
              type="file"
              accept=".jpg,.jpeg,.tif,.tiff,.png,.gif,image/jpeg,image/png,image/gif,image/tiff"
              onChange={(e) => setClubLogo(e.target.files?.[0] || null)}
            />
            <p className="field-note">
              JPG, JPEG, TIFF, PNG, or GIF. Cropped from the center to 400×400 pixels, expanding or contracting until the last side touches the limit. The 150×150 thumbnail is a resize of that same image.
            </p>
            {editingClub ? (
              <p className="field-note">If you upload a new image, it replaces the current one.</p>
            ) : null}
          </Form.Group>
          <AdminFormActions
            saveLabel="Save club"
            onCancel={() => {
              clearClubForm();
              setClubOpen(false);
            }}
          />
        </Form>
        </AdminFormModal>
        <div className="admin-sheet-wrap">
          <table className="admin-sheet">
            <thead>
              <tr>
                <th>Logo</th>
                <th>Club</th>
                <th>Location</th>
                <th>Phone</th>
                <th colSpan={2}>
                  <div className="admin-sheet-head">
                    <span>Email</span>
                    <AdminAddButton
                      onClick={() => {
                        clearClubForm();
                        setError("");
                        setNotice("");
                        setClubOpen(true);
                      }}
                    >
                      <svg className="admin-sheet-add-icon" viewBox="0 0 16 16" aria-hidden="true">
                        <path fill="currentColor" d="M7.1 1.4h1.8v5h5v1.8h-5v5H7.1v-5h-5V6.4h5v-5z" />
                      </svg>
                      Add a club
                    </AdminAddButton>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {clubs.length === 0 ? (
                <tr>
                  <td colSpan={6}>No clubs yet.</td>
                </tr>
              ) : (
                clubs.map((item) => (
                  <tr key={item.id}>
                    <td>
                      {item.logo_thumb_url ? (
                        <img className="admin-sheet-thumb" src={item.logo_thumb_url} alt="" />
                      ) : null}
                    </td>
                    <td className="admin-sheet-name">{item.title}</td>
                    <td>{item.location}</td>
                    <td>{item.phone}</td>
                    <td>{item.email1}</td>
                    <td>
                      <RecordActions
                        name={item.title}
                        onEdit={() => editClub(item)}
                        onRemove={async () => {
                          const removed = await removeRow(`/api/admin/clubs/${item.id}/`, "club", item.title);
                          if (removed && editingClub?.id === item.id) {
                            clearClubForm();
                            setClubOpen(false);
                          }
                        }}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
            </>
          )}

          {section === "car-classes" && (
            <>
        <h2>Car classes</h2>
        <p>Classes a car can enter. An empty year or make list means any.</p>
        <AdminAddButton
          onClick={() => {
            clearClassForm();
            setError("");
            setNotice("");
            setClassOpen(true);
          }}
        >
          Add a car class
        </AdminAddButton>
        <div className="class-filter" role="group" aria-label="Filter classes">
          {[
            ["all", "All"],
            ["concours", "Concours"],
            ["other", "Not Concours"],
          ].map(([id, label]) => (
            <button
              key={id}
              type="button"
              className={classFilter === id ? "is-on" : undefined}
              aria-pressed={classFilter === id}
              onClick={() => setClassFilter(id)}
            >
              {label}
            </button>
          ))}
        </div>
        <AdminFormModal
          show={classOpen}
          size="lg"
          title={editingClass ? `Edit ${editingClass.title}` : "Add a car class"}
          onHide={() => {
            clearClassForm();
            setClassOpen(false);
          }}
        >
        <Form onSubmit={addClass}>
          <Form.Group className="mb-3" controlId="classTitle">
            <Form.Label>Class name</Form.Label>
            <Form.Control value={classTitle} onChange={(e) => setClassTitle(e.target.value)} required />
          </Form.Group>
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3" controlId="classType">
                <Form.Label>Type</Form.Label>
                <Form.Select value={classType} onChange={(e) => changeClassType(e.target.value)}>
                  <option value="all">All</option>
                  <option value="automobile">Automobile</option>
                  <option value="motorcycle">Motorcycle</option>
                </Form.Select>
                <p className="field-note">
                  All shows every make. Automobile hides motorcycle-only makes. Motorcycle hides automobile-only makes.
                </p>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3" controlId="yearStart">
                <Form.Label>Year start</Form.Label>
                <Form.Control
                  type="number"
                  value={yearStart}
                  onChange={(e) => setYearStart(e.target.value)}
                  placeholder="Any"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3" controlId="yearEnd">
                <Form.Label>Year end</Form.Label>
                <Form.Control
                  type="number"
                  value={yearEnd}
                  onChange={(e) => setYearEnd(e.target.value)}
                  placeholder="Any"
                />
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-3 make-builds" controlId="classConcours">
            <Form.Check
              type="checkbox"
              id="classConcours"
              label="Concours class"
              checked={classConcours}
              onChange={(e) => setClassConcours(e.target.checked)}
            />
            {classConcours ? (
              <>
                <ul className="class-division-list">
                  <li><strong>Street/Race</strong> — first, second, and third</li>
                  <li><strong>Concours</strong> — first, second, and third</li>
                </ul>
                <p className="field-note">Only a car marked Concours can enter this class.</p>
              </>
            ) : (
              <p className="field-note">Winner and runner-up. Any fitting car can enter.</p>
            )}
          </Form.Group>
          <Form.Group className="mb-3">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <Form.Label className="mb-0">Makes</Form.Label>
              <Button
                type="button"
                variant="outline-secondary"
                size="sm"
                onClick={selectAllMakes}
                disabled={visibleMakes.length === 0}
              >
                {visibleMakes.length > 0 && visibleMakes.every((item) => classMakes.includes(item.id))
                  ? "Clear"
                  : "Select all"}
              </Button>
            </div>
            {makes.length === 0 ? (
              <div>Add a make before limiting a class to one.</div>
            ) : visibleMakes.length === 0 ? (
              <div>No makes of this type yet.</div>
            ) : (
              <div className="class-make-grid">
                {visibleMakes.map((item) => {
                  const selected = classMakes.includes(item.id);
                  return (
                    <button
                      key={item.id}
                      type="button"
                      className={`class-make${selected ? " selected" : ""}`}
                      aria-pressed={selected}
                      onClick={() => toggleMake(item.id)}
                    >
                      {item.logo_thumb_url ? (
                        <img src={item.logo_thumb_url} alt="" />
                      ) : (
                        <span className="class-make-logo" />
                      )}
                      <span className="class-make-name">{item.title}</span>
                    </button>
                  );
                })}
              </div>
            )}
            <p className="field-note">Leave every logo unselected to allow any make.</p>
          </Form.Group>
          <AdminFormActions
            saveLabel="Save car class"
            onCancel={() => {
              clearClassForm();
              setClassOpen(false);
            }}
          />
        </Form>
        </AdminFormModal>
        {classes.length === 0 ? (
          <Message variant="info" className="mt-4">No car classes yet.</Message>
        ) : shownClasses.length === 0 ? (
          <p className="field-note">No classes in this filter.</p>
        ) : (
          <div className="admin-records">
            {shownClasses.map((item) => (
              <article key={item.id} className="admin-record">
                <div className="admin-record-copy">
                  <strong>{item.title}</strong>
                  <span>
                    {classTypeLabel[item.vehicle_type] || item.vehicle_type}
                    {" · "}
                    {item.concours ? "Concours" : "Not Concours"}
                    {" · "}
                    {item.year_start || "any"} – {item.year_end || "any"}
                  </span>
                  <span>{item.manufacture_names.length ? item.manufacture_names.join(", ") : "Any make"}</span>
                </div>
                <RecordActions
                  name={item.title}
                  onEdit={() => editClass(item)}
                  onRemove={async () => {
                    const removed = await removeRow(`/api/admin/car-classes/${item.id}/`, "car class", item.title);
                    if (removed && editingClass?.id === item.id) {
                      clearClassForm();
                      setClassOpen(false);
                    }
                  }}
                />
              </article>
            ))}
          </div>
        )}
            </>
          )}

          {section === "events" && (
            <AdminEvents token={userInfo.token} classes={classes} />
          )}
          {section === "types" && <AdminTypes token={userInfo.token} />}
          {section === "home" && <AdminHome token={userInfo.token} />}
          {section === "show" && <AdminShow token={userInfo.token} />}
          {section === "company" && <AdminCompany token={userInfo.token} />}
          {section === "vip" && <AdminVip token={userInfo.token} />}
        </div>
      </Col>
    </Row>
  );
}

export default AdminDashboardScreen;
